import React, { useEffect, useState } from "react";
import { Shield, Plus, Trash2, Loader2, Crown, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AdminAccess() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v || !v.includes("@")) { toast.error("Enter a valid Google email"); return; }
    setSaving(true);
    try { await api.post("/admin/users", { email: v }); setEmail(""); toast.success(`Authorized ${v}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to add"); }
    finally { setSaving(false); }
  };

  const remove = async (em) => {
    if (!window.confirm(`Revoke admin access for ${em}?`)) return;
    try { await api.delete(`/admin/users/${encodeURIComponent(em)}`); toast.success("Access revoked"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to remove"); }
  };

  if (!data) return <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>;

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h1 className="font-display font-extrabold text-3xl tracking-tight flex items-center gap-3"><Shield size={26} /> Access</h1>
        <p className="text-muted-foreground text-sm mt-2">Authorize which Google accounts can sign in to this admin panel. Anyone signing in with a non-authorized Google account will be denied.</p>
      </div>

      <form onSubmit={add} className="flex gap-3 mb-8" data-testid="add-admin-form">
        <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@gmail.com" className="flex-1 bg-card border border-border px-4 py-3 text-sm outline-none focus:border-foreground transition-colors" />
        <button data-testid="add-admin-btn" disabled={saving} className="flex items-center gap-2 bg-foreground text-background px-5 py-3 text-[12px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Authorize
        </button>
      </form>

      <div className="border border-border bg-card divide-y divide-border">
        {data.users.map((u) => (
          <div key={u.email} className="flex items-center gap-4 px-5 py-4" data-testid={`admin-user-${u.email}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${u.is_owner ? "bg-onyx-wood text-onyx-black" : "bg-secondary text-foreground"}`}>
              {u.is_owner ? <Crown size={16} /> : <User size={16} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{u.email}{u.email === data.current && <span className="text-muted-foreground font-normal"> (you)</span>}</div>
              <div className="text-xs text-muted-foreground">{u.is_owner ? "Owner · permanent access" : `Authorized${u.added_by ? ` by ${u.added_by}` : ""}`}</div>
            </div>
            {u.is_owner ? (
              <span className="text-[10px] tracking-wide uppercase text-muted-foreground border border-border px-2.5 py-1">Owner</span>
            ) : (
              <button onClick={() => remove(u.email)} data-testid={`remove-admin-${u.email}`} className="p-2 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={16} /></button>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-4">New members: have them sign in once with Google (they'll be denied), then add their email here — they can sign in immediately after.</p>
    </div>
  );
}
